import { BarChart } from '@mui/x-charts'
import React, { useEffect, useMemo, useState } from 'react'
import { formatDate } from 'date-fns/format'
import { Grid, Typography } from '@mui/material'
import { getMuiDataGridLocaleText, useAppLocale } from '../../../i18n/localeUtils'
import { useTranslation } from 'react-i18next'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { useTheme } from '@mui/material/styles'
import { USER_EVENT_TYPES } from '@finance-lab/utils/types'
import RadioGroupLabeled from '../RadioGroupLabeled'
import { EnrichedIssueEntity } from '../../../types/IssueEntity'
import { formatBigMoney, formatNumber } from '../../../packages/utils/numberFormatters'
import { filterIssueForGraphs, getDateForIssue } from '../utils'
import { useLogUserEvent } from '../../../hooks/useLogUserEvent'
import { issueFiltersAtom, IssueFilterType } from '../../../atoms/issueAtom'
import { useAtom } from 'jotai'
import { exportDataGridProps } from './utils'

interface BarChartSerieData {
    date: Date
    directedIssue: number
    rightsIssue: number
    ipo: number
    [key: string]: number | Date
}

export const IssueMarket = ({
    graphSelector,
    isLoading,
    issues,
}: {
    graphSelector: React.ReactNode
    isLoading: boolean
    issues: EnrichedIssueEntity[]
}) => {
    const theme = useTheme()
    const {
        t,
        i18n: { language: locale },
    } = useTranslation()
    const { dateFnsLocale } = useAppLocale()

    const [selectedTimeUnit, setSelectedTimeUnit] = useState<'quarter' | 'month'>('quarter')
    const [selectedYAxisOption, setSelectedYAxisOption] = useState<'count' | 'raisedSize' | 'offerSize'>('count')
    const [oldFilter, setOldFilter] = useState<IssueFilterType | null>(null)
    const [, setFilters] = useAtom(issueFiltersAtom)
    const yAxisLabels = {
        count: t('site.table.count'),
        raisedSize: t('pages.statistics.issueMarket.raisedSize'),
        offerSize: t('pages.statistics.issueMarket.offerSize'),
    }

    useEffect(() => {
        setFilters((prevFilters) => {
            if (!oldFilter) setOldFilter(prevFilters)

            return {
                ...prevFilters,
                issueTypes: {
                    value: prevFilters.issueTypes.value.filter((type) =>
                        ['Directed issue', 'IPO', 'Rights issue'].includes(type),
                    ),
                    disabledItems: ['Non-cash issue'],
                },
            }
        })

        return () => {
            if (oldFilter) setFilters((filters) => ({ ...filters, issueTypes: { ...oldFilter.issueTypes } }))
        }
    }, [setFilters, setOldFilter, oldFilter])

    useLogUserEvent(USER_EVENT_TYPES.STATS_TREND_GRAPHS_SELECTION, {
        selectedTimeUnit,
        selectedYAxisOption,
    })

    const columns = [
        {
            field: 'time',
            headerName: '',
        },
        {
            field: 'directedIssue',
            headerName: t('charts.issueType.RE'),
        },
        {
            field: 'rightsIssue',
            headerName: t('charts.issueType.FE'),
        },
        {
            field: 'ipo',
            headerName: t('charts.issueType.IPO'),
        },
    ]

    const serieValueFormatter = React.useCallback(
        (value: number | null) => {
            if (value === null) return '-'

            if (selectedYAxisOption === 'count') return formatNumber(value, { decimals: 0, locale })

            return formatBigMoney(value, { locale, currencySymbol: 'SEK' })
        },
        [locale, selectedYAxisOption],
    )

    const series = [
        {
            dataKey: 'directedIssue',
            id: 'directedIssue',
            label: t('charts.issueType.RE'),
            color: theme.palette.RE.main,
            valueFormatter: serieValueFormatter,
            stack: 'stack-1',
        },
        {
            dataKey: 'rightsIssue',
            id: 'rightsIssue',
            label: t('charts.issueType.FE'),
            color: theme.palette.FE.main,
            valueFormatter: serieValueFormatter,
            stack: 'stack-1',
        },
        {
            dataKey: 'ipo',
            id: 'ipo',
            label: t('charts.issueType.IPO'),
            color: theme.palette.IPO.main,
            valueFormatter: serieValueFormatter,
            stack: 'stack-1',
        },
    ]

    const groupedDataset = useMemo(
        () =>
            Object.values(
                issues
                    .filter(
                        (issue) =>
                            filterIssueForGraphs(issue) &&
                            ((issue.unitSubPriceSek !== null && issue.totalOfferedUnits !== null) ||
                                issue.abbTargetSek !== null),
                    )
                    .reduce((acc: Record<string, BarChartSerieData>, issue: EnrichedIssueEntity) => {
                        const issueTypeKey = ['Directed issue', 'ABB Directed issue'].includes(issue.type)
                            ? 'directedIssue'
                            : issue.type === 'Rights issue'
                              ? 'rightsIssue'
                              : issue.type === 'IPO'
                                ? 'ipo'
                                : 'not-interesting'
                        if (issueTypeKey === 'not-interesting') return acc

                        const {
                            abbTargetSek,
                            unitSubTotal,
                            overAllotmentAllocation,
                            unitSubPriceSek,
                            totalOfferedUnits,
                            secondaryOffering,
                            secondarySub,
                        } = issue as Pick<
                            EnrichedIssueEntity,
                            | 'abbTargetSek'
                            | 'overAllotmentAllocation'
                            | 'totalOfferedUnits'
                            | 'secondaryOffering'
                            | 'secondarySub'
                        > & {
                            unitSubTotal: number
                            unitSubPriceSek: string
                        }
                        const publicationDate = getDateForIssue(issue)
                        const dateKey = formatDate(
                            new Date(publicationDate as string),
                            selectedTimeUnit === 'quarter' ? 'yyyy QQQ' : 'yyyy MM',
                            { locale: dateFnsLocale },
                        )

                        const values = {
                            raisedSize:
                                (unitSubTotal + (overAllotmentAllocation ?? 0) + (secondaryOffering ?? 0)) *
                                +unitSubPriceSek,
                            offerSize:
                                (totalOfferedUnits !== null || secondarySub !== null) && unitSubPriceSek !== null
                                    ? ((totalOfferedUnits ?? 0) + (secondarySub ?? 0)) * +unitSubPriceSek
                                    : abbTargetSek,
                            count: 1,
                        }

                        if (values[selectedYAxisOption] === null) return acc

                        const dateGroup = acc[dateKey] || {
                            id: dateKey,
                            date: publicationDate,
                            directedIssue: 0,
                            ipo: 0,
                            rightsIssue: 0,
                            time: dateKey,
                        }
                        const value = dateGroup[issueTypeKey] + +values[selectedYAxisOption]

                        return {
                            ...acc,
                            [dateKey]: {
                                ...dateGroup,
                                [issueTypeKey]: value,
                            },
                        }
                    }, {}),
            ).sort((a, b) => (a.date > b.date ? 1 : -1)),
        [issues, selectedTimeUnit, selectedYAxisOption, dateFnsLocale],
    )

    const tableRows = groupedDataset.map(({ date, ...row }) =>
        selectedYAxisOption === 'count'
            ? row
            : {
                  ...row,
                  directedIssue: row.directedIssue ? formatBigMoney(row.directedIssue, { locale }) : '-',
                  ipo: row.ipo ? formatBigMoney(row.ipo, { locale }) : '-',
                  rightsIssue: row.rightsIssue ? formatBigMoney(row.rightsIssue, { locale }) : '-',
              },
    )

    return (
        <Grid container spacing={10}>
            <Grid size={{ xs: 12, md: 6 }}>
                <Grid container>
                    <Grid size={12}>{graphSelector}</Grid>
                    <Grid size={5}>
                        <RadioGroupLabeled<'quarter' | 'month'>
                            label={t('pages.statistics.issueMarket.timeUnit')}
                            onChange={setSelectedTimeUnit}
                            options={{
                                quarter: t('charts.timeUnit.quarter'),
                                month: t('charts.timeUnit.month'),
                            }}
                            value={selectedTimeUnit}
                        />
                    </Grid>
                    <Grid size={7}>
                        <RadioGroupLabeled<'count' | 'raisedSize' | 'offerSize'>
                            label={t('pages.statistics.issueMarket.yAxis')}
                            onChange={setSelectedYAxisOption}
                            options={yAxisLabels}
                            value={selectedYAxisOption}
                        />
                    </Grid>
                </Grid>
                <BarChart
                    dataset={groupedDataset}
                    loading={isLoading}
                    xAxis={[{ scaleType: 'band', dataKey: 'time' }]}
                    series={series}
                    height={500}
                ></BarChart>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant='h6' gutterBottom>
                    {yAxisLabels[selectedYAxisOption]}
                </Typography>
                <DataGridPro
                    {...exportDataGridProps}
                    columns={columns}
                    loading={isLoading}
                    localeText={getMuiDataGridLocaleText(locale)}
                    rows={tableRows}
                    hideFooter
                />
            </Grid>
        </Grid>
    )
}
